import React from "react";
import { useNavigate } from "react-router-dom";
import Logo from "./Logo";
import { Button } from "./ui/Button";
import { ArrowsClockwise, ArrowRight, Info } from "@phosphor-icons/react";
import { clearDemoMode } from "../demo/demoInterceptor";

/**
 * Iter 20.08 — DemoLayout.
 *
 * Schlanker Layout für die public `/demo/*`-Routes. Kein Sidebar, kein
 * Live-View, kein Chat-Bubble — nur:
 *   • gelber Demo-Banner ganz oben
 *   • Logo links
 *   • „Demo neu starten" + „Vollversion testen" rechts
 *   • Content
 *
 * Bewusst OHNE useAuth(), damit die Public-Route ohne Login funktioniert.
 */
export default function DemoLayout({ children, caseTitle, wide = false }) {
  const navigate = useNavigate();

  const handleRestart = () => {
    // Session-Key beibehalten (User bleibt in Demo-Kontext); Landing zeigt
    // die Auswahl-Karten wieder an.
    navigate("/demo");
  };

  const handleExitToLogin = () => {
    clearDemoMode();
    // Iter 20.08b — Force-Reload notwendig: sessionStorage-Löschung räumt
    // den Demo-Marker, ABER der AuthContext.user (DEMO_USER) bleibt im
    // React-State. Bei einem simplen navigate('/login') würde
    // PublicOnlyRoute den „aktiven" DEMO_USER erkennen und auf /dashboard
    // umleiten. window.location.href erzwingt einen frischen AuthProvider-
    // Mount, dessen fetchMe() jetzt (ohne Demo-Marker) den echten
    // /auth/me-Call macht und user=null setzt.
    window.location.href = "/login";
  };

  return (
    <div
      className="min-h-screen bg-ink-50 flex flex-col"
      data-testid="demo-layout"
    >
      {/* Gelber Demo-Banner */}
      <div
        className="bg-brand-400 text-ink-900 border-b border-brand-500"
        data-testid="demo-banner"
      >
        <div
          className={[
            wide ? "max-w-7xl" : "max-w-6xl",
            "mx-auto px-6 lg:px-10 py-2 flex flex-wrap items-center gap-3",
          ].join(" ")}
        >
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold">
            <Info size={13} weight="bold" />
            Demo-Modus
          </div>
          <div className="text-[12px] font-medium">
            {caseTitle ? (
              <>
                Beispiel-Daten: <span className="font-bold">{caseTitle}</span>{" "}
                — keine echten Daten, kein Speichern.
              </>
            ) : (
              <>Beispiel-Daten, keine echten Auswertungen.</>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={handleRestart}
              className="inline-flex items-center gap-1 rounded-sm bg-ink-900/10 hover:bg-ink-900/20 px-2 py-1 text-[11px] font-semibold text-ink-900 transition-crisp"
              data-testid="demo-banner-restart"
              title="Zurück zur Fall-Auswahl"
            >
              <ArrowsClockwise size={11} weight="bold" />
              Fall wechseln
            </button>
            <button
              type="button"
              onClick={handleExitToLogin}
              className="inline-flex items-center gap-1 rounded-sm bg-ink-900 hover:bg-ink-800 px-2.5 py-1 text-[11px] font-semibold text-white transition-crisp"
              data-testid="demo-banner-exit"
            >
              Vollversion testen
              <ArrowRight size={11} weight="bold" />
            </button>
          </div>
        </div>
      </div>

      {/* Top-Bar mit Logo */}
      <div className="border-b border-ink-200 bg-white">
        <div
          className={[
            wide ? "max-w-7xl" : "max-w-6xl",
            "mx-auto px-6 lg:px-10 py-4 flex items-center justify-between",
          ].join(" ")}
        >
          <button
            type="button"
            onClick={() => navigate("/")}
            className="inline-flex transition-crisp hover:opacity-80"
            title="Zur Startseite"
            data-testid="demo-logo"
          >
            <Logo size="md" />
          </button>
          <div className="hidden sm:block text-xs text-warm-500">
            Live-Demo &middot; ohne Anmeldung
          </div>
        </div>
      </div>

      {/* Main content */}
      <main
        className={[
          wide ? "max-w-7xl" : "max-w-6xl",
          "flex-1 w-full mx-auto px-6 lg:px-10 py-8",
        ].join(" ")}
        data-testid="demo-main"
      >
        {children}
      </main>

      {/* Bottom-CTA */}
      <footer
        className="border-t border-ink-200 bg-white"
        data-testid="demo-footer"
      >
        <div
          className={[
            wide ? "max-w-7xl" : "max-w-6xl",
            "mx-auto px-6 lg:px-10 py-6 flex flex-wrap items-center justify-between gap-4",
          ].join(" ")}
        >
          <div className="text-xs text-warm-600 leading-relaxed max-w-lg">
            <strong className="text-ink-900">Bereit für den Ernstfall?</strong>{" "}
            In der Vollversion arbeiten Sie mit Ihren echten Kollektivverträgen,
            Vordienstzeiten und PDF-Berichten.
          </div>
          <Button
            variant="brand"
            size="md"
            onClick={handleExitToLogin}
            data-testid="demo-footer-cta"
          >
            Jetzt kostenlos testen
            <ArrowRight size={12} weight="bold" />
          </Button>
        </div>
      </footer>
    </div>
  );
}
